/**
 * Link href classification and localization helpers shared by link atoms.
 *
 * Why this exists:
 * The link atom used by the learn screens (LinkComponent inside Button/Link)
 * applies the request locale as a prefix ('/{locale}' + href) so root-relative
 * internal links become localized, crawlable URLs (e.g. '/explainer/{slug}' →
 * '/en/explainer/{slug}'). Applied unconditionally, that same prefixing
 * corrupts absolute external URLs: 'https://doi.org/10.1037/...' hydrated as
 * '/en/https://doi.org/10.1037/...', which both breaks the link and causes a
 * hydration mismatch because the server HTML carried the raw absolute href.
 *
 * The rule encoded here is the single source of truth for that boundary:
 *
 * - Absolute http(s) URLs, protocol-relative URLs (//host/...), mailto:, tel:,
 *   and pure hash hrefs (#anchor) are EXTERNAL/same-shape links: they are
 *   returned verbatim and must be rendered as plain anchors with
 *   target="_blank" + rel="noopener noreferrer" where appropriate, on both the
 *   server and the client so SSR and hydration agree byte-for-byte.
 * - Root-relative hrefs starting with '/' are INTERNAL: they receive the
 *   '/{locale}' prefix (locale 'en' may optionally stay unprefixed to match
 *   the proxy rewrites used by the learn pages).
 * - Anything else (empty string, malformed input) is returned unchanged so a
 *   bad href degrades to an inert anchor rather than a broken one.
 *
 * All functions are pure and deterministic (no window checks, no Date/Math),
 * so server and client always produce identical attributes.
 */

/** Href schemes/segments that must never receive a locale prefix. */
const ABSOLUTE_URL_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
const PROTOCOL_RELATIVE_RE = /^\/\//;
const HASH_ONLY_RE = /^#[^\s]*$/;

/**
 * True when the href must be rendered as an external anchor (plain <a>),
 * i.e. it is an absolute URL, protocol-relative, a mail/tel link, or a pure
 * in-page hash. Internal root-relative paths return false.
 */
export function isExternalHref(href: unknown): boolean {
  if (typeof href !== 'string') return false;
  const trimmed = href.trim();
  if (!trimmed) return false;
  if (PROTOCOL_RELATIVE_RE.test(trimmed)) return true;
  if (HASH_ONLY_RE.test(trimmed)) return true;
  if (trimmed.startsWith('/')) return false;
  return ABSOLUTE_URL_RE.test(trimmed);
}

/**
 * Apply the locale prefix to a root-relative internal href.
 * Returns the href unchanged when it is external, not a rooted path, or
 * already locale-prefixed (so double prefixing is impossible).
 *
 * The prefix is omitted for the null locale and for the default 'en' locale,
 * matching the learn proxy rewrites where unprefixed URLs are English.
 */
export function localizeHref(
  href: unknown,
  locale?: string | null,
): string {
  if (typeof href !== 'string') return '';
  const trimmed = href.trim();
  if (!trimmed) return '';

  // External hrefs are returned verbatim — never prefixed.
  if (isExternalHref(trimmed)) return trimmed;

  // Only rooted internal paths get the prefix.
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return trimmed;

  const normalizedLocale =
    typeof locale === 'string' && locale.trim() && locale.trim() !== 'en'
      ? locale.trim().replace(/\\/g, '')
      : null;
  if (!normalizedLocale) return trimmed;

  // Guard against double-prefixing when a caller already localized the href.
  const firstSegment = trimmed.slice(1).split('/')[0] ?? '';
  if (firstSegment && firstSegment.toLowerCase() === normalizedLocale.toLowerCase()) {
    return trimmed;
  }

  return `/${normalizedLocale}${trimmed}`;
}

export interface ResolvedLinkHref {
  /** Final href to render, identical on server and client. */
  href: string;
  /** True when the link must render as a plain external anchor. */
  external: boolean;
}

/**
 * Resolve a payload href into the exact anchor attributes the atom must
 * render. Using this single helper guarantees the server-rendered anchor and
 * the hydrated client anchor are identical:
 *
 * - internal href → locale-prefixed, rendered through the router Link.
 * - external href → raw URL, rendered as <a> with target="_blank" and
 *   rel="noopener noreferrer" so the attributes survive hydration.
 */
export function resolveLocalizedHref(
  href: unknown,
  locale?: string | null,
): ResolvedLinkHref {
  const value = typeof href === 'string' ? href.trim() : '';
  if (!value) return { href: '', external: true };
  return {
    href: localizeHref(value, locale),
    external: isExternalHref(value),
  };
}